import { useQuery } from '@apollo/client';
import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Pie, PieChart, Tooltip } from 'recharts';

import { GetEdnaDataDocument } from '@/__generated__/graphql';
import ednaBee from '@/images/ednaBee.jpg';
import ednaHoneySample from '@/images/ednaHoneySample.jpg';
import ednaScience from '@/images/ednaScience.png';
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

import { GROWTH_GROUPS, useGrowthGroups } from './GrowthDonutChart.js';

interface GrowthGroup { label: MyHiveLocalizationKey; color: string; fill: string; value: number; pct: number; topPlants: string[] }

function GrowthTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: GrowthGroup }> }): React.JSX.Element | null {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];

  if (!active || !payload?.length) return null;
  const g = payload[0]?.payload;
  if (!g || g.value === 0) return null;
  return (
    <div
      className="rounded-xl px-4 py-3 text-left shadow-lg"
      style={{ backgroundColor: '#013331', border: 'none', minWidth: 180 }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: g.color }} />
        <span className="font-brand-md text-sm" style={{ color: 'rgba(255,255,255,0.95)' }}>{t[g.label]}</span>
      </div>
      <p className="font-brand-sm text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
        {g.pct}{t[MyHiveLocalizationKey.EdnaGrowthPctOfGroups]}
      </p>
      {g.topPlants.length > 0 && (
        <p className="font-brand-sm text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
          {g.topPlants.join(', ')}
        </p>
      )}
    </div>
  );
}

const SITES = [
  { pageId: 'i8NHrFcoeK', city: 'New York City' },
  { pageId: 'a24rnDspo7',  city: 'Los Angeles' },
  { pageId: 'UDcTxLCYqe', city: 'San Diego' },
  { pageId: 'SNM0cgM083', city: 'Seattle' },
  { pageId: 'EqOELEKA5a', city: 'Washington, D.C.' },
] as const;

const PLANT_RECOMMENDATIONS: Record<string, Array<{ family: string; example: string }>> = {
  'i8NHrFcoeK': [
    { family: 'Carrot family (Apiaceae)',    example: 'golden alexanders, Zizia aurea' },
    { family: 'Spiderwort family (Commelinaceae)', example: 'spiderwort, Tradescantia virginiana' },
    { family: 'Buckthorn family (Rhamnaceae)', example: 'New Jersey tea, Ceanothus americanus' },
    { family: 'Vervain family (Verbenaceae)', example: 'blue vervain, Verbena hastata' },
  ],
  'a24rnDspo7': [
    { family: 'Heath family (Ericaceae)',      example: 'bigberry manzanita, Arctostaphylos glauca' },
    { family: 'Barberry family (Berberidaceae)', example: 'Oregon grape, Berberis aquifolium' },
    { family: 'Buckwheat family (Polygonaceae)', example: 'California buckwheat, Eriogonum fasciculatum' },
    { family: 'Phlox family (Polemoniaceae)', example: 'globe gilia, Gilia capitata' },
  ],
  'UDcTxLCYqe': [
    { family: 'Milkweed family (Apocynaceae)', example: 'narrowleaf milkweed, Asclepias fascicularis' },
    { family: 'Poppy family (Papaveraceae)',   example: 'California poppy, Eschscholzia californica' },
    { family: 'Heath family (Ericaceae)',      example: 'bigberry manzanita, Arctostaphylos glauca' },
    { family: 'Phlox family (Polemoniaceae)', example: 'globe gilia, Gilia capitata' },
  ],
  'SNM0cgM083': [
    { family: 'Asparagus family (Asparagaceae)', example: 'common camas, Camassia quamash' },
    { family: 'Mallow family (Malvaceae)',     example: 'meadow checkermallow, Sidalcea campestris' },
    { family: 'Heath family (Ericaceae)',      example: 'salal, Gaultheria shallon' },
    { family: 'Phlox family (Polemoniaceae)', example: 'large-flowered collomia, Collomia grandiflora' },
  ],
  'EqOELEKA5a': [
    { family: 'Heath family (Ericaceae)',       example: 'highbush blueberry, Vaccinium corymbosum' },
    { family: 'Plantain family (Plantaginaceae)', example: 'smooth penstemon, Penstemon digitalis' },
    { family: 'Bellflower family (Campanulaceae)', example: 'great blue lobelia, Lobelia siphilitica' },
    { family: 'Gentian family (Gentianaceae)', example: 'bottle gentian, Gentiana andrewsii' },
  ],
};

function CityDropdown({ pageId, setPageId }: { pageId: string; setPageId: (id: string) => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = SITES.find(s => s.pageId === pageId) ?? SITES[0];

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 font-brand-heavy text-gray-900 border-b-2 border-gray-400 hover:border-gray-700 transition-colors cursor-pointer bg-transparent outline-none pb-0.5"
        style={{ fontSize: 'inherit', fontFamily: 'inherit', lineHeight: 'inherit' }}
      >
        {selected.city}
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          style={{ display: 'inline', verticalAlign: 'middle', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
        >
          <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <span
          className="absolute left-0 top-full mt-2 z-50 flex flex-col overflow-hidden rounded-xl shadow-xl"
          style={{ backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.08)', minWidth: '100%', whiteSpace: 'nowrap' }}
        >
          {SITES.map(s => (
            <button
              key={s.pageId}
              onClick={() => { setPageId(s.pageId); setOpen(false); }}
              className="font-brand-md text-left px-5 py-3 text-sm hover:bg-gray-50 transition-colors"
              style={{
                color: s.pageId === pageId ? 'var(--color-brand-teal-dark-500)' : '#374151',
                fontWeight: s.pageId === pageId ? 700 : 400,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {s.city}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export default function EDNAPage(): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const [pageId, setPageId] = useState<string>(SITES[0].pageId);

  const { data } = useQuery(GetEdnaDataDocument, {
    variables: { pageId },
    skip: !pageId,
  });

  const edna = data?.getEdnaData;

  const growthGroups = useGrowthGroups(edna?.detections ?? []);

  return (
    <div className="bg-brand-soft-gray">
      <Helmet>
        <title>Honey eDNA · Measured Biodiversity Evidence</title>
        <meta name="title" content="Honey eDNA · Measured Biodiversity Evidence" />
        <meta name="description" content="See the plant species detected directly from the DNA in a year of this building's honey." />
        <link rel="icon" type="image/png" href={`${import.meta.env['VITE_SSG_BASE_URL']}aura_favicon.png`} />
      </Helmet>

      {/* First screen */}
      <div
        className="relative flex flex-col items-center justify-center px-4 text-center"
        style={{ minHeight: 'calc(100vh - var(--nav-height, 0px))' }}
      >
        {/* Label */}
        <p
          className="font-brand-sm text-sm uppercase tracking-widest mb-6"
          style={{ color: 'var(--color-brand-teal-dark-500)' }}
        >
          Honey EDNA&nbsp;&middot;&nbsp;Measured Biodiversity Evidence
        </p>

        {/* Location headline */}
        <h1 className="font-brand-xl text-3xl sm:text-4xl text-gray-700 leading-snug mb-8 max-w-xl">
          Honey eDNA results from an office building in {' '}
          <CityDropdown pageId={pageId} setPageId={setPageId} />.
        </h1>

        {/* Big number */}
        <p
          className="font-brand-2xl leading-none mb-4 tabular-nums"
          style={{
            fontSize: 'clamp(10rem, 28vw, 22rem)',
            color: 'var(--color-brand-teal-dark-500)',
            letterSpacing: '-0.03em',
          }}
        >
          {edna ? edna.totalUniqueHits : (
            <span className="inline-block w-48 h-36 bg-gray-200 rounded-xl animate-pulse align-middle" />
          )}
        </p>

        {/* Caption */}
        <p className="font-brand-md text-2xl sm:text-3xl text-gray-700 mb-4">
          kinds of plants, growing all{' '}
          <strong className="font-brand-heavy italic text-gray-900">around you.</strong>
        </p>

        <p className="font-brand-sm text-base text-gray-500 leading-relaxed max-w-xs sm:max-w-sm">
          Every plant the bees foraged, read from the DNA in a year of this building&apos;s honey,
        </p>
      </div>

      {/* 01 · Growth form breakdown */}
      <div className="border-t border-gray-300 bg-white" style={{ minHeight: 'calc(100vh - var(--nav-height, 0px))' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 h-full flex flex-col items-center justify-center text-center">

          {/* Heading */}
          <h2 className="font-brand-xl text-3xl sm:text-4xl text-gray-900 mb-3">Here&apos;s what they found.</h2>
          <p className="font-brand-sm text-sm text-gray-500 leading-relaxed mb-12 max-w-sm">
            A whole landscape, grouped by how they grow. Hover over any slice to see what&apos;s inside.
          </p>

          {/* Donut chart */}
          <div className="relative mb-12" style={{ width: 300, height: 300 }}>
            <PieChart width={300} height={300}>
              <Pie
                data={growthGroups.filter(g => g.value > 0)}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              />
              <Tooltip content={<GrowthTooltip />} wrapperStyle={{ zIndex: 10 }} allowEscapeViewBox={{ x: true, y: true }} isAnimationActive={false} />
            </PieChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
              <p className="font-brand-2xl text-5xl text-gray-900 tabular-nums leading-none">{edna?.totalUniqueHits ?? '—'}</p>
              <p className="font-brand-sm text-xs text-gray-400 mt-1 uppercase tracking-widest" style={{ whiteSpace: 'pre-line' }}>{t[MyHiveLocalizationKey.EdnaGrowthKindsByForm]}</p>
            </div>
          </div>

          {/* Legend rows */}
          <div className="w-full flex flex-col gap-3">
            {growthGroups.map(g => (
              <div
                key={g.label}
                className="flex items-center gap-4 rounded-lg px-4 py-3 bg-gray-50 border border-gray-200"
              >
                <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: g.color }} />
                <span className="font-brand-md text-sm text-gray-700 w-40 text-left">{t[g.label]}</span>
                <span className="font-brand-sm flex-1 text-xs text-gray-400 text-left">
                  {g.topPlants.length > 0 ? g.topPlants.join(', ') : '—'}
                </span>
                <span className="font-brand-sm text-sm text-gray-500 tabular-nums">
                  {g.value > 0 ? `${g.pct}%` : '—'}
                </span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 02 · How we know */}
      <div
        className="flex flex-col items-center justify-center px-4 sm:px-8 py-16 text-center"
        style={{
          minHeight: 'calc(100vh - var(--nav-height, 0px))',
          backgroundColor: 'var(--color-brand-teal-dark-500)',
        }}
      >
        <div className="max-w-3xl w-full mx-auto flex flex-col items-center">

          {/* Label */}
          <p className="font-brand-sm text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--color-brand-yellow)' }}>
            How we know
          </p>

          {/* Heading */}
          <h2 className="font-brand-xl text-3xl sm:text-4xl mb-12" style={{ color: 'rgba(255,255,255,0.95)' }}>
            The proof is in the{' '}
            <em className="font-brand-heavy not-italic" style={{ color: 'var(--color-brand-yellow)' }}>honey.</em>
          </h2>

          {/* Three cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full mb-12">
            {[
              {
                n: '1',
                title: 'A season of foraging',
                body: 'Pooled into one honey sample, a whole year together.',
              },
              {
                n: '2',
                title: 'Read straight from the DNA',
                body: 'The DNA is matched to the plant families, groups, and species it came from.',
              },
              {
                n: '3',
                title: 'Built into your reporting',
                body: 'Every finding flows into your Aura report, ready for TNFD, GRESB, and CSRD.',
              },
            ].map(({ n, title, body }) => (
              <div
                key={n}
                className="flex flex-col items-center px-6 py-8 text-center"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  clipPath: 'polygon(16px 0%, calc(100% - 16px) 0%, 100% 16px, 100% calc(100% - 16px), calc(100% - 16px) 100%, 16px 100%, 0% calc(100% - 16px), 0% 16px)',
                }}
              >
                <p className="font-brand-lg text-4xl mb-4" style={{ color: 'var(--color-brand-teal-300)' }}>{n}</p>
                <p className="font-brand-md text-sm mb-3" style={{ color: 'rgba(255,255,255,0.9)' }}>{title}</p>
                <p className="font-brand-sm text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{body}</p>
              </div>
            ))}
          </div>

          {/* Footer note */}
          <p className="font-brand-sm text-sm leading-relaxed max-w-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Bees forage a{' '}
            <strong className="font-brand-heavy" style={{ color: 'rgba(255,255,255,0.85)' }}>2 to 5 km radius</strong>
            , so your honey reflects the surrounding landscape.
          </p>

          {/* Photo strip */}
          <div className="mt-10 grid grid-cols-3 gap-4 w-full">
            {[
              { src: ednaBee, alt: 'Bee foraging on a wildflower' },
              { src: ednaHoneySample, alt: 'Honey sample being collected from a hive frame' },
              { src: ednaScience, alt: 'Lab analysis of honey DNA samples' },              
            ].map(({ src, alt }) => (
              <div key={alt} className="aspect-square overflow-hidden rounded-xl">
                <img src={src} alt={alt} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 03 · The full list */}
      <div
        className="flex flex-col px-4 sm:px-8 py-16"
        style={{ minHeight: 'calc(100vh - var(--nav-height, 0px))', backgroundColor: 'var(--color-brand-soft-gray)' }}
      >
        <div className="max-w-2xl w-full mx-auto flex flex-col flex-1">

          {/* Heading */}
          <div className="text-center mb-8 shrink-0">
            <p className="font-brand-sm text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--color-brand-teal-500)' }}>
              Every plant we found
            </p>
            <h2 className="font-brand-xl text-3xl sm:text-4xl text-gray-900 mb-3">The full list.</h2>
            <p className="font-brand-sm text-sm text-gray-500 leading-relaxed max-w-sm mx-auto">
              Every plant group the bees brought back. Scroll the whole list.
            </p>
          </div>

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto flex flex-col gap-2 pr-1" style={{ maxHeight: '60vh' }}>
            {!edna ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-4 rounded-xl px-4 py-4 shrink-0 animate-pulse"
                style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.06)' }}
              >
                <div className="w-16 h-16 rounded-lg bg-gray-200 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="h-4 bg-gray-200 rounded w-36 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-24" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-16 shrink-0" />
              </div>
            )) : edna.detections.map(detection => (
              <div
                key={detection.latinTaxon}
                className="flex items-center gap-4 rounded-xl px-4 py-4 shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.06)' }}
              >
                {/* Photo */}
                <div
                  className="w-16 h-16 rounded-lg shrink-0 overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
                >
                  {detection.imgUrl ? (
                    <img src={detection.imgUrl} alt={detection.commonNameEn} className="w-full h-full object-cover" />
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="1" y="1" width="22" height="22" stroke="rgba(0,0,0,0.2)" strokeWidth="1" fill="none" />
                      <line x1="1" y1="1" x2="23" y2="23" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
                      <line x1="23" y1="1" x2="1" y2="23" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
                    </svg>
                  )}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-brand-md text-base text-gray-800 leading-snug mb-0.5">{detection.commonNameEn}</p>
                  <p className="font-brand-sm text-sm text-gray-400 italic">{detection.latinTaxon}</p>
                </div>

                {/* Growth habit tag */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: GROWTH_GROUPS.find(g => g.test(detection.growthHabit))?.color ?? '#c8c7bf' }}
                  />
                  <span className="font-brand-sm text-sm text-gray-500">{detection.growthHabit}</span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* 04 · A few good plants to add */}

      <div
        className="flex flex-col items-center justify-center px-4 sm:px-8 py-16 bg-white border-t border-gray-100"
        style={{ minHeight: 'calc(100vh - var(--nav-height, 0px))' }}
      >
        <div className="max-w-2xl w-full mx-auto flex flex-col items-center text-center">

          {/* Label */}
          <p className="font-brand-sm text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--color-brand-teal-500)' }}>
            What you can do with it
          </p>

          {/* Heading */}
          <h2 className="font-brand-xl text-3xl sm:text-4xl text-gray-900 mb-4">
            A few good plants to <strong className="font-brand-heavy">add.</strong>
          </h2>

          {/* Subtitle */}
          <p className="font-brand-sm text-sm text-gray-500 leading-relaxed max-w-sm mb-10">
            Bee-friendly plant families well suited to this region that the bees haven&apos;t brought back yet. Worth adding to the landscape around you.
          </p>

          {/* Plant rows */}
          <div className="w-full flex flex-col gap-3 mb-8">
            {(PLANT_RECOMMENDATIONS[pageId] ?? []).map(({ family, example }) => (
              <div
                key={family}
                className="flex items-center gap-4 rounded-xl px-5 py-4 text-left"
                style={{ backgroundColor: 'var(--color-brand-soft-gray)', border: '1px solid rgba(0,0,0,0.04)' }}
              >
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: 'var(--color-brand-warm-orange-500)' }} />
                <span className="font-brand-md text-sm text-gray-800 w-52 shrink-0">{family}</span>
                <span className="font-brand-sm text-sm text-gray-500 italic">{example}</span>
              </div>
            ))}
          </div>

          <p className="font-brand-sm text-xs text-gray-400">Recommendations curated for this region.</p>

        </div>
      </div>

      {/* 05 · CTA */}
      <div
        className="flex flex-col items-center justify-center px-4 sm:px-8 py-20 text-center"
        style={{ backgroundColor: 'var(--color-brand-teal-dark-500)' }}
      >
        <h2 className="font-brand-xl text-3xl sm:text-4xl mb-3 max-w-sm leading-snug" style={{ color: 'rgba(255,255,255,0.95)' }}>
          Curious what&apos;s growing around{' '}
          <em className="font-brand-heavy not-italic" style={{ color: 'var(--color-brand-yellow)' }}>your building?</em>
        </h2>
        <p className="font-brand-sm text-sm mb-8" style={{ color: 'rgba(255,255,255,0.45)' }}>
          It starts with a single hive.
        </p>
        <a
          href="https://aura.alveole.com/book-a-demo"
          target="_blank"
          rel="noopener noreferrer"
          className="font-brand-md inline-flex items-center gap-3 px-6 py-3 text-sm rounded"
          style={{
            backgroundColor: 'var(--color-brand-yellow)',
            color: 'var(--color-brand-teal-dark-500)',
            border: '1.5px solid var(--color-brand-yellow)',
          }}
        >
          Book an intro call
          <span>&#8594;</span>
        </a>
      </div>

    </div>
  );
}
